import { Routes } from '@angular/router';
import { canActivateAuthRole } from './core/guards/auth-guard';
import { canActivatePlatformAdmin, canMatchPlatformBackOffice } from './core/guards/platform-guard';
import { canDeactivateDirtyForm } from './core/guards/dirty-form.guard';
import { isFeatureEnabled } from './core/config/runtime-config';
import { COLLECTIONS_FLAG } from './features/collections/config/collection-routes.config';
import { Component, inject } from '@angular/core';
import { Router } from '@angular/router';

/** Temporary placeholder for routes that don't have pages yet */
@Component({
  template: `<div style="padding: 40px; text-align: center; color: var(--color-text-muted); font-size: 1.1rem;">
    <p style="font-size: 1.5rem; font-weight: 600; margin-bottom: 8px;">📄 {{ currentPath }}</p>
    <p>This page is not implemented yet.</p>
  </div>`,
})
class PlaceholderPage {
  protected currentPath = inject(Router).url;
}

export const routes: Routes = [
  {
    path: '',
    redirectTo: '/dashboard',
    pathMatch: 'full'
  },
  {
    path: 'dashboard',
    title: 'Dashboard',
    loadComponent: () => import('./features/dashboard/pages/dashboard/dashboard.page').then(m => m.DashboardPage),
    canActivate: [canActivateAuthRole],
  },
  {
    path: 'settings/general',
    loadComponent: () => import('./features/settings/pages/settings-general/settings-general.page').then(m => m.SettingsGeneralPage),
    canActivate: [canActivateAuthRole],
    canDeactivate: [canDeactivateDirtyForm],
  },
  {
    path: 'settings/locations',
    loadComponent: () => import('./features/locations/pages/locations-list/locations-list.page').then(m => m.LocationsPage),
    canActivate: [canActivateAuthRole],
  },
  {
    path: 'settings/locations/new',
    loadComponent: () => import('./features/locations/pages/location-form/location-form.page').then(m => m.LocationFormPage),
    canActivate: [canActivateAuthRole],
    canDeactivate: [canDeactivateDirtyForm],
  },
  {
    path: 'settings/locations/edit/:id',
    loadComponent: () => import('./features/locations/pages/location-form/location-form.page').then(m => m.LocationFormPage),
    canActivate: [canActivateAuthRole],
    canDeactivate: [canDeactivateDirtyForm],
  },
  // ─── Categories ───
  {
    path: 'catalog/categories',
    title: 'Categories',
    loadComponent: () => import('./features/categories/pages/categories-list/categories-list.page').then(m => m.CategoriesPage),
    canActivate: [canActivateAuthRole],
  },
  {
    path: 'catalog/categories/new',
    title: 'New Category',
    loadComponent: () => import('./features/categories/pages/category-form/category-form.page').then(m => m.CategoryFormPage),
    canActivate: [canActivateAuthRole],
    canDeactivate: [canDeactivateDirtyForm],
  },
  {
    path: 'catalog/categories/edit/:id',
    title: 'Edit Category',
    loadComponent: () => import('./features/categories/pages/category-form/category-form.page').then(m => m.CategoryFormPage),
    canActivate: [canActivateAuthRole],
    canDeactivate: [canDeactivateDirtyForm],
  },
  // ─── Collections (online store menu; hidden until the storefront ships) ───
  {
    path: 'catalog/collections',
    title: 'Collections',
    loadComponent: () => import('./features/collections/pages/collections-list/collections-list.page').then(m => m.CollectionsListPage),
    canMatch: [() => isFeatureEnabled(COLLECTIONS_FLAG)],
    canActivate: [canActivateAuthRole],
  },
  {
    path: 'catalog/collections/new',
    title: 'New Collection',
    loadComponent: () => import('./features/collections/pages/collection-form/collection-form.page').then(m => m.CollectionFormPage),
    canMatch: [() => isFeatureEnabled(COLLECTIONS_FLAG)],
    canActivate: [canActivateAuthRole],
    canDeactivate: [canDeactivateDirtyForm],
  },
  {
    path: 'catalog/collections/edit/:id',
    title: 'Edit Collection',
    loadComponent: () => import('./features/collections/pages/collection-form/collection-form.page').then(m => m.CollectionFormPage),
    canMatch: [() => isFeatureEnabled(COLLECTIONS_FLAG)],
    canActivate: [canActivateAuthRole],
    canDeactivate: [canDeactivateDirtyForm],
  },
  {
    path: 'catalog/navigation',
    title: 'Navigation',
    loadComponent: () => import('./features/collections/pages/navigation/navigation.page').then(m => m.NavigationPage),
    canMatch: [() => isFeatureEnabled(COLLECTIONS_FLAG)],
    canActivate: [canActivateAuthRole],
    canDeactivate: [canDeactivateDirtyForm],
  },
  // ─── Groups ───
  {
    path: 'settings/users-permissions/groups',
    loadComponent: () => import('./features/groups/pages/groups-list/groups.page').then(m => m.GroupsPage),
    canActivate: [canActivateAuthRole],
  },
  {
    path: 'settings/users-permissions/groups/new',
    loadComponent: () => import('./features/groups/pages/group-form/group-form.page').then(m => m.GroupFormPage),
    canActivate: [canActivateAuthRole],
    canDeactivate: [canDeactivateDirtyForm],
  },
  {
    path: 'settings/users-permissions/groups/edit/:id',
    loadComponent: () => import('./features/groups/pages/group-form/group-form.page').then(m => m.GroupFormPage),
    canActivate: [canActivateAuthRole],
    canDeactivate: [canDeactivateDirtyForm],
  },
  {
    path: 'settings/users-permissions/groups/:id',
    loadComponent: () => import('./features/groups/pages/group-detail/group-detail.page').then(m => m.GroupDetailPage),
    canActivate: [canActivateAuthRole],
  },
  // ─── Roles ───
  {
    path: 'settings/users-permissions/roles',
    loadComponent: () => import('./features/roles/pages/roles-list/roles.page').then(m => m.RolesPage),
    canActivate: [canActivateAuthRole],
  },
  {
    path: 'settings/users-permissions/roles/new',
    loadComponent: () => import('./features/roles/pages/role-form/role-form.page').then(m => m.RoleFormPage),
    canActivate: [canActivateAuthRole],
    canDeactivate: [canDeactivateDirtyForm],
  },
  {
    path: 'settings/users-permissions/roles/edit/:id',
    loadComponent: () => import('./features/roles/pages/role-form/role-form.page').then(m => m.RoleFormPage),
    canActivate: [canActivateAuthRole],
    canDeactivate: [canDeactivateDirtyForm],
  },
  {
    path: 'settings/users-permissions/roles/:id',
    loadComponent: () => import('./features/roles/pages/role-detail/role-detail.page').then(m => m.RoleDetailPage),
    canActivate: [canActivateAuthRole],
  },
  // ─── Users ───
  {
    path: 'settings/users-permissions/users',
    loadComponent: () => import('./features/users/pages/users-list/users.page').then(m => m.UsersPage),
    canActivate: [canActivateAuthRole],
  },
  {
    path: 'settings/users-permissions/users/invite',
    loadComponent: () => import('./features/users/pages/user-form/user-form.page').then(m => m.UserFormPage),
    canActivate: [canActivateAuthRole],
    canDeactivate: [canDeactivateDirtyForm],
  },
  {
    path: 'settings/users-permissions/users/edit/:id',
    loadComponent: () => import('./features/users/pages/user-form/user-form.page').then(m => m.UserFormPage),
    canActivate: [canActivateAuthRole],
    canDeactivate: [canDeactivateDirtyForm],
  },
  {
    path: 'settings/users-permissions/users/:id',
    loadComponent: () => import('./features/users/pages/user-detail/user-detail.page').then(m => m.UserDetailPage),
    canActivate: [canActivateAuthRole],
  },
  // ─── Suppliers ───
  {
    path: 'procurement/suppliers',
    title: 'Suppliers',
    loadComponent: () => import('./features/suppliers/pages/suppliers-list/suppliers-list.page').then(m => m.SuppliersListPage),
    canActivate: [canActivateAuthRole],
  },
  {
    path: 'procurement/suppliers/new',
    title: 'New Supplier',
    loadComponent: () => import('./features/suppliers/pages/supplier-form/supplier-form.page').then(m => m.SupplierFormPage),
    canActivate: [canActivateAuthRole],
    canDeactivate: [canDeactivateDirtyForm],
  },
  {
    path: 'procurement/suppliers/edit/:id',
    title: 'Edit Supplier',
    loadComponent: () => import('./features/suppliers/pages/supplier-form/supplier-form.page').then(m => m.SupplierFormPage),
    canActivate: [canActivateAuthRole],
    canDeactivate: [canDeactivateDirtyForm],
  },
  {
    path: 'procurement/suppliers/:id',
    title: 'Supplier',
    loadComponent: () => import('./features/suppliers/pages/supplier-profile/supplier-profile.page').then(m => m.SupplierProfilePage),
    canActivate: [canActivateAuthRole],
  },
  // ─── Purchase Orders ───
  {
    path: 'procurement/purchase-orders',
    title: 'Purchase Orders',
    loadComponent: () => import('./features/purchase-orders/pages/purchase-order-list/purchase-order-list.page').then(m => m.PurchaseOrderListPage),
    canActivate: [canActivateAuthRole],
  },
  {
    path: 'procurement/purchase-orders/new',
    title: 'New Purchase Order',
    loadComponent: () => import('./features/purchase-orders/pages/purchase-order-form/purchase-order-form.page').then(m => m.PurchaseOrderFormPage),
    canActivate: [canActivateAuthRole],
    canDeactivate: [canDeactivateDirtyForm],
  },
  {
    path: 'procurement/purchase-orders/:id',
    title: 'Purchase Order',
    loadComponent: () => import('./features/purchase-orders/pages/purchase-order-detail/purchase-order-detail.page').then(m => m.PurchaseOrderDetailPage),
    canActivate: [canActivateAuthRole],
  },
  {
    path: 'procurement/purchase-orders/:id/edit',
    title: 'Edit Purchase Order',
    loadComponent: () => import('./features/purchase-orders/pages/purchase-order-form/purchase-order-form.page').then(m => m.PurchaseOrderFormPage),
    canActivate: [canActivateAuthRole],
    canDeactivate: [canDeactivateDirtyForm],
  },
  {
    path: 'procurement/purchase-orders/:id/receive',
    title: 'Receive Goods',
    loadComponent: () => import('./features/purchase-orders/pages/receive-goods/receive-goods.page').then(m => m.ReceiveGoodsPage),
    canActivate: [canActivateAuthRole],
    canDeactivate: [canDeactivateDirtyForm],
  },
  // ─── Customers ───
  {
    path: 'customers',
    title: 'Customers',
    loadComponent: () => import('./features/customers/pages/customers-list/customers-list.page').then(m => m.CustomersListPage),
    canActivate: [canActivateAuthRole],
  },
  {
    path: 'customers/new',
    title: 'New Customer',
    loadComponent: () => import('./features/customers/pages/customer-form/customer-form.page').then(m => m.CustomerFormPage),
    canActivate: [canActivateAuthRole],
    canDeactivate: [canDeactivateDirtyForm],
  },
  {
    path: 'customers/edit/:id',
    title: 'Edit Customer',
    loadComponent: () => import('./features/customers/pages/customer-form/customer-form.page').then(m => m.CustomerFormPage),
    canActivate: [canActivateAuthRole],
    canDeactivate: [canDeactivateDirtyForm],
  },
  // ─── Orders ───
  {
    path: 'orders',
    title: 'Orders',
    loadComponent: () => import('./features/orders/pages/orders-list/orders-list.page').then(m => m.OrdersListPage),
    canActivate: [canActivateAuthRole],
  },
  {
    path: 'orders/new',
    title: 'New Order',
    loadComponent: () => import('./features/orders/pages/order-form/order-form.page').then(m => m.OrderFormPage),
    canActivate: [canActivateAuthRole],
    canDeactivate: [canDeactivateDirtyForm],
  },
  {
    path: 'orders/:id',
    title: 'Order',
    loadComponent: () => import('./features/orders/pages/order-detail/order-detail.page').then(m => m.OrderDetailPage),
    canActivate: [canActivateAuthRole],
  },
  // ─── POS ───
  // To enable capability-based access control, uncomment the `data` line below
  // AFTER provisioning these roles in Keycloak realm `business`:
  //   pos:access, pos:sell, pos:hold, pos:discount, pos:return
  // See pos-bugs-v1.md → POS-RISK-1, and core/services/permission.service.ts.
  {
    path: 'pos',
    title: 'Point of Sale',
    loadComponent: () => import('./features/pos/pages/pos/pos.page').then(m => m.PosPage),
    canActivate: [canActivateAuthRole],
    // data: { requiredKeys: ['pos:access'] },
  },
  // ─── Products ───
  {
    path: 'catalog/products',
    title: 'Products',
    loadComponent: () => import('./features/products/pages/products-list/products-list.page').then(m => m.ProductsListPage),
    canActivate: [canActivateAuthRole],
  },
  {
    path: 'catalog/products/new',
    title: 'New Product',
    loadComponent: () => import('./features/products/pages/product-form/product-form.page').then(m => m.ProductFormPage),
    canActivate: [canActivateAuthRole],
    canDeactivate: [canDeactivateDirtyForm],
  },
  {
    path: 'catalog/products/edit/:id',
    title: 'Edit Product',
    loadComponent: () => import('./features/products/pages/product-form/product-form.page').then(m => m.ProductFormPage),
    canActivate: [canActivateAuthRole],
    canDeactivate: [canDeactivateDirtyForm],
  },
  {
    path: 'catalog/products/:id',
    title: 'Product',
    loadComponent: () => import('./features/products/pages/product-profile/product-profile.page').then(m => m.ProductProfilePage),
    canActivate: [canActivateAuthRole],
  },
  // ─── Inventory ───
  {
    path: 'inventory',
    title: 'Inventory',
    loadComponent: () => import('./features/inventory/pages/inventory-overview/inventory-overview.page').then(m => m.InventoryOverviewPage),
    canActivate: [canActivateAuthRole],
  },
  {
    path: 'inventory/movements/:skuId',
    title: 'Stock Movements',
    loadComponent: () => import('./features/inventory/pages/movements/movements.page').then(m => m.MovementsPage),
    canActivate: [canActivateAuthRole],
  },
  {
    path: 'inventory/adjustments',
    title: 'Adjustments',
    loadComponent: () => import('./features/inventory/pages/adjustments/adjustments.page').then(m => m.AdjustmentsPage),
    canActivate: [canActivateAuthRole],
  },
  {
    path: 'inventory/adjustment-reasons',
    title: 'Adjustment Reasons',
    loadComponent: () => import('./features/inventory/pages/adjustment-reasons/adjustment-reasons.page').then(m => m.AdjustmentReasonsPage),
    canActivate: [canActivateAuthRole],
  },
  {
    path: 'inventory/skus/:skuId',
    title: 'SKU Inventory',
    loadComponent: () => import('./features/inventory/pages/sku-inventory-detail/sku-inventory-detail.page').then(m => m.SkuInventoryDetailPage),
    canActivate: [canActivateAuthRole],
  },
  // ─── Analytics ───
  {
    path: 'analytics',
    redirectTo: '/analytics/sales',
    pathMatch: 'full',
  },
  {
    path: 'analytics/sales',
    title: 'Sales Report',
    loadComponent: () => import('./features/analytics/pages/sales-report/sales-report.page').then(m => m.SalesReportPage),
    canActivate: [canActivateAuthRole],
  },
  {
    path: 'analytics/profit',
    title: 'Profit Report',
    loadComponent: () => import('./features/analytics/pages/profit-report/profit-report.page').then(m => m.ProfitReportPage),
    canActivate: [canActivateAuthRole],
  },
  // ─── My Profile ───
  {
    path: 'settings/me',
    loadComponent: () => import('./features/users/pages/my-profile/my-profile.page').then(m => m.MyProfilePage),
    canActivate: [canActivateAuthRole],
  },
  // ─── Platform back office ───
  // canMatch, not canActivate: with platform_backoffice off the router keeps
  // looking and the URL falls through to the catch-all, so the back office does
  // not exist rather than existing and refusing.
  {
    path: 'platform',
    title: 'Platform back office',
    canMatch: [canMatchPlatformBackOffice],
    loadComponent: () => import('./features/platform/pages/back-office-layout/back-office-layout.page').then(m => m.BackOfficeLayoutPage),
    canActivate: [canActivatePlatformAdmin],
    children: [
      {
        path: '',
        loadComponent: () => import('./features/platform/pages/platform-shell/platform-shell.page').then(m => m.PlatformShellPage),
      },
      {
        path: 'tenants',
        title: 'Tenants',
        loadComponent: () => import('./features/platform/pages/tenants-list/tenants-list.page').then(m => m.TenantsListPage),
      },
      {
        path: 'tenants/new',
        title: 'Create a tenant',
        loadComponent: () => import('./features/platform/pages/tenant-create/tenant-create.page').then(m => m.TenantCreatePage),
      },
      // After 'tenants/new', so the wizard keeps that path rather than reading as a tenant id.
      {
        path: 'tenants/:id',
        title: 'Tenant',
        loadComponent: () => import('./features/platform/pages/tenant-detail/tenant-detail.page').then(m => m.TenantDetailPage),
      },
      {
        path: 'audit',
        title: 'Audit log',
        loadComponent: () => import('./features/platform/pages/audit-log/audit-log.page').then(m => m.AuditLogPage),
      },
      {
        path: 'health',
        title: 'Platform health',
        loadComponent: () => import('./features/platform/pages/platform-health/platform-health.page').then(m => m.PlatformHealthPage),
      },
    ],
  },
  // Catch-all: render placeholder for any unimplemented route
  {
    path: '**',
    component: PlaceholderPage,
  },
];
